import { useEffect, useRef } from 'react';

import { useFormContext, useFormWatch } from '@american-autonomy/ag-ui';

import { FIELD_UPDATE_OPTIONS } from './constants';
import type {
    ApplicationFormSchema,
    BoundaryFieldSchema,
    FormPathValue,
    Item,
    UseBoundarySyncParams
} from './types';

export const useFieldBoundarySync = <T extends ApplicationFormSchema>({
    boundaryPath,
    clientPath,
    control,
    farmPath,
    fieldPath,
    onFetchSiteHierarchy
}: UseBoundarySyncParams<T>) => {
    const { setValue } = useFormContext<T>();
    const prevSiteIdRef = useRef<string | undefined>(undefined);
    const prevBoundarySiteIdRef = useRef<string | undefined>(undefined);

    const field = useFormWatch<T>({ control, name: fieldPath }) as Item | undefined;
    const boundary = useFormWatch<T>({ control, name: boundaryPath }) as
        | BoundaryFieldSchema
        | undefined;

    const siteId = field?.value;
    const boundarySiteId = boundary?.siteId;

    useEffect(() => {
        const siteIdChanged = prevSiteIdRef.current !== siteId;
        const boundarySiteIdChanged = prevBoundarySiteIdRef.current !== boundarySiteId;

        const handleFieldChange = () => {
            if (
                siteIdChanged &&
                boundary &&
                (!siteId || (boundarySiteId && siteId !== boundarySiteId))
            ) {
                setValue(boundaryPath, null as FormPathValue<T, typeof boundaryPath>);
            }
        };

        const handleBoundaryChange = async () => {
            if (boundarySiteIdChanged && boundarySiteId && !siteId) {
                const data = await onFetchSiteHierarchy(boundarySiteId);

                if (!data) {
                    return;
                }

                setValue(
                    clientPath,
                    data.client as FormPathValue<T, typeof clientPath>,
                    FIELD_UPDATE_OPTIONS
                );

                setValue(
                    farmPath,
                    data.farm as FormPathValue<T, typeof farmPath>,
                    FIELD_UPDATE_OPTIONS
                );

                setValue(
                    fieldPath,
                    data.field as FormPathValue<T, typeof fieldPath>,
                    FIELD_UPDATE_OPTIONS
                );
            }
        };

        handleFieldChange();
        handleBoundaryChange();

        prevSiteIdRef.current = siteId;
        prevBoundarySiteIdRef.current = boundarySiteId;
    }, [
        siteId,
        boundarySiteId,
        boundary,
        boundaryPath,
        clientPath,
        farmPath,
        fieldPath,
        setValue,
        onFetchSiteHierarchy
    ]);

    return { boundarySiteId, siteId };
};
